import { useState, useEffect } from 'react';

type LoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

// Status shared across all hook instances
let globalStatus: LoadingStatus = 'idle';
let loadPromise: Promise<void> | null = null;

/**
 * Hook to load the Google Charts library script and corechart package.
 * Ensures the script is loaded only once.
 *
 * @returns The current loading status: 'idle', 'loading', 'loaded', 'error'.
 */
export const useGoogleChartsLoader = (): LoadingStatus => {
  const [status, setStatus] = useState<LoadingStatus>(globalStatus);

  useEffect(() => {
    // If already loaded or loading started by another instance, just sync state
    if (globalStatus === 'loaded' || globalStatus === 'loading') {
      setStatus(globalStatus);
      // If loading, wait for the promise to resolve/reject
      if (globalStatus === 'loading' && loadPromise) {
        loadPromise.then(() => setStatus('loaded')).catch(() => setStatus('error'));
      }
      return;
    }

    // Start loading if idle
    if (globalStatus === 'idle') {
      globalStatus = 'loading';
      setStatus('loading');

      loadPromise = new Promise<void>((resolve, reject) => {
        // Check if script already exists (e.g., added manually or by another library)
        if (document.querySelector('script[src="https://www.gstatic.com/charts/loader.js"]')) {
          // Assume it's loaded or will be loaded, proceed to load packages
          if (typeof google !== 'undefined' && google.charts) {
            google.charts.load('current', { packages: ['corechart'] });
            google.charts.setOnLoadCallback(() => {
              globalStatus = 'loaded';
              setStatus('loaded');
              resolve();
            });
          } else {
            // If google object exists but not charts, wait for it
            // This is a fallback, might need refinement based on specific scenarios
            const interval = setInterval(() => {
              if (typeof google !== 'undefined' && google.charts) {
                clearInterval(interval);
                google.charts.load('current', { packages: ['corechart'] });
                google.charts.setOnLoadCallback(() => {
                  globalStatus = 'loaded';
                  setStatus('loaded');
                  resolve();
                });
              }
            }, 100);
          }
          return; // Exit, script tag exists
        }

        const script = document.createElement('script');
        script.src = 'https://www.gstatic.com/charts/loader.js';
        script.async = true;
        script.defer = true; // Ensure script executes after HTML is parsed

        script.onload = () => {
          if (typeof google !== 'undefined' && google.charts) {
            google.charts.load('current', { packages: ['corechart'] });
            google.charts.setOnLoadCallback(() => {
              globalStatus = 'loaded';
              setStatus('loaded'); // Update local state as well
              resolve();
            });
          } else {
            console.error('Google Charts loader loaded, but google.charts is not available.');
            globalStatus = 'error';
            setStatus('error');
            reject(new Error('Google Charts API not available after script load.'));
          }
        };

        script.onerror = (error) => {
          console.error('Failed to load Google Charts script:', error);
          globalStatus = 'error';
          setStatus('error');
          reject(error);
        };

        document.head.appendChild(script);
      });
    }
  }, []); // Empty dependency array ensures this runs only once per component instance

  return status;
};
